import StraightenIcon from "@mui/icons-material/Straighten";
import { Box, Stack, Typography } from "@mui/material";

import { RoutePointDetail } from "components/Shipment/components/RoutePointDetail";
import { ShipmentDetail } from "components/Shipment/components/ShipmentDetail";
import { SuggestedNavigationRouteMap } from "components/ShipmentOverviewTab/components/RouteTab/components/SuggestedNavigationRouteMap";
import {
  getRouteDestination,
  getRouteOrigin,
  isRouteCreatedAfterNavigationRollout,
  meterToKilometer,
  meterToMile,
  removeDecimalPoint,
  toCityProvinceCountryAndZipReadablePointLocation,
} from "utils";

import type { SubmitQuotePreviewShipment } from "./types";

/**
 * The stop's location, rendered without consulting the viewer's role.
 *
 * `RoutePointDetail`'s default display branches on role, and its carrier branch
 * reads the in-app carrier shipment context — which this page never mounts, so a
 * signed-in carrier got a numbered stop with a date and no address at all. What
 * this carrier may see was already decided server-side by the time the payload
 * arrived, so the page renders it the same way for everyone who can open it.
 */
function StopLocation(props: { routePoint: RoutePoint }) {
  const { routePoint } = props;
  return (
    <Typography variant="h6">
      {toCityProvinceCountryAndZipReadablePointLocation(
        routePoint.point_location,
      )}
    </Typography>
  );
}

interface ShipmentRouteSectionProps {
  shipment: SubmitQuotePreviewShipment;
}

interface PointCoords {
  longitude: number;
  latitude: number;
  point_number: number;
}

function toCoords(point: RoutePoint): PointCoords | null {
  const { latitude, longitude } = point.point_location;
  if (latitude == null || longitude == null) {
    return null;
  }
  const latitudeNumber = Number(latitude);
  const longitudeNumber = Number(longitude);
  if (!Number.isFinite(latitudeNumber) || !Number.isFinite(longitudeNumber)) {
    return null;
  }
  return {
    latitude: latitudeNumber,
    longitude: longitudeNumber,
    point_number: point.point_number,
  };
}

/**
 * Every stop on the run, then the suggested route on a map.
 *
 * Modelled on components/PublicShipment/components/PublicShipmentRoute rather
 * than the in-app route tab: the public shipment page is also unauthenticated,
 * so it has already answered the map-token and no-session questions this page
 * would otherwise hit first.
 *
 * Rendering first and last only — which is all the carrier condensed shipment
 * carries — made a multi-stop run look like a single pickup and drop, with
 * nothing on screen saying a stop had been left out (#2975 §3).
 */
export function ShipmentRouteSection(props: ShipmentRouteSectionProps) {
  const { shipment } = props;
  const { route_points: routePoints, navigation } = shipment;

  if (routePoints.length === 0) {
    return null;
  }

  const origin = getRouteOrigin(routePoints);
  const destination = getRouteDestination(routePoints);
  const midPoints = routePoints
    .filter(
      (routePoint) =>
        routePoint.stop_type !== "ORIGIN" &&
        routePoint.stop_type !== "DESTINATION",
    )
    .sort((a, b) => a.point_number - b.point_number);

  const originCoords = origin != null ? toCoords(origin) : null;
  const destinationCoords = destination != null ? toCoords(destination) : null;
  const midPointCoords = midPoints
    .map(toCoords)
    .filter((pointCoords): pointCoords is PointCoords => pointCoords !== null);

  const canRenderMap =
    navigation != null && originCoords != null && destinationCoords != null;

  return (
    <Stack direction="column" spacing={2}>
      <Stack direction="column" width="100%">
        {origin != null && (
          <RoutePointDetail
            routePoint={origin}
            displayRoutePointDates
            locationDisplay={<StopLocation routePoint={origin} />}
          />
        )}
        {midPoints.map((routePoint) => (
          <Box key={routePoint.point_number}>
            <RoutePointDetail
              routePoint={routePoint}
              displayRoutePointDates
              locationDisplay={<StopLocation routePoint={routePoint} />}
            />
          </Box>
        ))}
        {destination != null && (
          <RoutePointDetail
            routePoint={destination}
            displayRoutePointDates
            locationDisplay={<StopLocation routePoint={destination} />}
          />
        )}
      </Stack>

      {canRenderMap ? (
        <Stack direction="column">
          <Stack direction="row" flexWrap="wrap" justifyContent="flex-start">
            <ShipmentDetail
              icon={<StraightenIcon />}
              title="Route Distance"
              text={`${removeDecimalPoint(meterToMile(navigation.distance_meters))} mi (${removeDecimalPoint(meterToKilometer(navigation.distance_meters))} kms)`}
            />
          </Stack>
          <SuggestedNavigationRouteMap
            navigation={navigation}
            origin={originCoords}
            destination={destinationCoords}
            midPoints={midPointCoords}
          />
          <Typography variant="body1" textAlign="center" sx={{ mt: ".5rem" }}>
            Route distance is approximate and may vary. Please verify as needed.
          </Typography>
        </Stack>
      ) : (
        <Typography textAlign="center" variant="body2" color="text.secondary">
          {isRouteCreatedAfterNavigationRollout(shipment.info.created_at)
            ? "Suggested route not available yet, please check back later."
            : "Suggested route not available for this shipment."}
        </Typography>
      )}
    </Stack>
  );
}
